/**
 * TableView - displays tasks in an editable spreadsheet-like table
 */

import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardStore } from '@stores/boardStore';
import { tasksApi, type Task } from '@services/api';
import { toast } from '@components/ui/Toast';

interface TableViewProps {
  onTaskClick: (taskId: string) => void;
  searchQuery?: string;
  filterPriority?: string;
  filterColumnId?: string;
}

type EditingCell = {
  taskId: string;
  field: 'title' | 'priority' | 'dueDate' | 'status';
} | null;

const priorityOptions = ['urgent', 'high', 'medium', 'low', 'none'];

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
  high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
  medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
  low: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
  none: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200',
};

export default function TableView({ onTaskClick, searchQuery = '', filterPriority = '', filterColumnId = '' }: TableViewProps) {
  const { t } = useTranslation();
  const { currentBoard, tasks, updateTask } = useBoardStore();

  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [editValue, setEditValue] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Get all tasks as array, filtered by search and filters
  const allTasks = useMemo(() => {
    const taskList: Task[] = [];
    Object.values(tasks).forEach((task) => {
      if (task) {
        // Apply priority filter
        if (filterPriority && task.priority !== filterPriority) return;
        // Apply column filter
        if (filterColumnId && task.columnId !== filterColumnId) return;
        // Apply search filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const matches =
            task.title.toLowerCase().includes(query) ||
            (task.description?.toLowerCase().includes(query) ?? false) ||
            (task.taskKey?.toLowerCase().includes(query) ?? false) ||
            (task.labels?.some(label => label.name.toLowerCase().includes(query)) ?? false);
          if (!matches) return;
        }
        taskList.push(task);
      }
    });
    return taskList.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }, [tasks, searchQuery, filterPriority, filterColumnId]);

  const handleStartEdit = (taskId: string, field: 'title' | 'priority' | 'dueDate' | 'status', value: string) => {
    setEditingCell({ taskId, field });
    setEditValue(value);
  };

  const handleSaveEdit = useCallback(async () => {
    if (!editingCell) return;

    const { taskId, field } = editingCell;
    const task = tasks[taskId];
    if (!task) return;

    try {
      let updateData: Partial<Task> = {};

      switch (field) {
        case 'title':
          if (editValue.trim() && editValue !== task.title) {
            updateData = { title: editValue.trim() };
          }
          break;
        case 'priority':
          if (editValue !== task.priority) {
            updateData = { priority: editValue as Task['priority'] };
          }
          break;
        case 'dueDate':
          if (editValue !== task.dueDate) {
            if (editValue) {
              updateData = { dueDate: editValue };
            }
          }
          break;
      }

      if (Object.keys(updateData).length > 0) {
        await tasksApi.update(taskId, updateData);
        updateTask(taskId, updateData);
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error(t('errors.generic', 'Failed to update task'));
      // Keep the cell in edit mode so the user can retry.
      return;
    }

    setEditingCell(null);
    setEditValue('');
  }, [editingCell, editValue, tasks, updateTask, t]);

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const toggleRowSelection = (taskId: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const toggleAllRows = () => {
    if (selectedRows.size === allTasks.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(allTasks.map((t) => t.id)));
    }
  };

  const getColumnName = (columnId: string): string => {
    const column = currentBoard?.columns.find((c) => c.id === columnId);
    return column?.name ?? 'Unknown';
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-800">
      {/* Toolbar */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {allTasks.length} task{allTasks.length !== 1 ? 's' : ''}
          </span>
          {selectedRows.size > 0 && (
            <span className="text-sm text-primary-600 dark:text-primary-400">
              {selectedRows.size} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedRows.size > 0 && (
            <>
              <button className="px-3 py-1 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md">
                Bulk Edit
              </button>
              <button className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md">
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
          <thead className="bg-neutral-50 dark:bg-neutral-900 sticky top-0">
            <tr>
              <th scope="col" className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedRows.size === allTasks.length && allTasks.length > 0}
                  onChange={toggleAllRows}
                  className="h-4 w-4 text-primary-600 rounded border-neutral-300 dark:border-neutral-600"
                />
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
              >
                Key
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider min-w-[300px]"
              >
                Title
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
              >
                Priority
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
              >
                Due Date
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
              >
                Type
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider"
              >
                Created
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
            {allTasks.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-neutral-500 dark:text-neutral-400">
                  No tasks found
                </td>
              </tr>
            ) : (
              allTasks.map((task) => (
                <tr
                  key={task.id}
                  className={`${
                    selectedRows.has(task.id) ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-neutral-50 dark:hover:bg-neutral-700'
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(task.id)}
                      onChange={() => toggleRowSelection(task.id)}
                      className="h-4 w-4 text-primary-600 rounded border-neutral-300 dark:border-neutral-600"
                    />
                  </td>
                  <td
                    className="px-4 py-3 text-sm text-primary-600 dark:text-primary-400 font-medium cursor-pointer hover:underline"
                    onClick={() => onTaskClick(task.id)}
                  >
                    {task.taskKey}
                  </td>
                  <td className="px-4 py-3">
                    {editingCell?.taskId === task.id &&
                    editingCell?.field === 'title' ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveEdit}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        className="w-full px-2 py-1 text-sm border border-primary-300 dark:border-primary-600 rounded focus:ring-1 focus:ring-primary-500 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                      />
                    ) : (
                      <div
                        className="text-sm text-neutral-900 dark:text-white cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-600 px-2 py-1 rounded"
                        onClick={() =>
                          handleStartEdit(task.id, 'title', task.title)
                        }
                      >
                        {task.title}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
                      {getColumnName(task.columnId)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {editingCell?.taskId === task.id &&
                    editingCell?.field === 'priority' ? (
                      <select
                        value={editValue}
                        onChange={(e) => {
                          setEditValue(e.target.value);
                          setEditingCell(null);
                          void tasksApi
                            .update(task.id, {
                              priority: e.target.value as Task['priority'],
                            })
                            .then(() => {
                              updateTask(task.id, {
                                priority: e.target.value as Task['priority'],
                              });
                            });
                        }}
                        onBlur={handleCancelEdit}
                        autoFocus
                        className="text-xs border border-primary-300 dark:border-primary-600 rounded focus:ring-1 focus:ring-primary-500 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                      >
                        {priorityOptions.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize cursor-pointer ${
                          priorityColors[task.priority] ?? priorityColors.none
                        }`}
                        onClick={() =>
                          handleStartEdit(task.id, 'priority', task.priority)
                        }
                      >
                        {task.priority}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {editingCell?.taskId === task.id &&
                    editingCell?.field === 'dueDate' ? (
                      <input
                        type="date"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleSaveEdit}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        className="text-sm border border-primary-300 dark:border-primary-600 rounded focus:ring-1 focus:ring-primary-500 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                      />
                    ) : (
                      <div
                        className="text-sm text-neutral-500 dark:text-neutral-400 cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-600 px-2 py-1 rounded"
                        onClick={() =>
                          handleStartEdit(
                            task.id,
                            'dueDate',
                            task.dueDate
                              ? new Date(task.dueDate)
                                  .toISOString()
                                  .split('T')[0] ?? ''
                              : ''
                          )
                        }
                      >
                        {task.dueDate
                          ? new Date(task.dueDate).toLocaleDateString()
                          : '-'}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400 capitalize">
                    {task.taskType}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                    {new Date(task.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
